import './BlogPage.scss';

import React, { useState, useEffect, useCallback } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  useDataQuery,
  useDataPagination,
  useGTMDataSend,
  QueryFilter,
} from 'common/hooks';
import { queryBlogFeed, queryBlogPage } from 'network';
import { SiteBlock } from '../../../common/components/SiteBlock/SiteBlock';
import { Grid, GridItem } from '@dataartdev/uikit/Grid';
import { Mq } from '@dataartdev/uikit/Mq';
import { Button } from '@dataartdev/uikit/Button';
import { Tag } from '@dataartdev/uikit/Tag';
import { Loader } from '@dataartdev/uikit/Loader';
import { Typography } from '@dataartdev/uikit/Typography';
import { IconFilter } from '@dataartdev/uikit/IconFilter';
import { DesktopFilters } from '../components/DesktopFilters';
import { useInfiniteScroll } from '@dataartdev/uikit/useInfiniteScroll';
import { getUrlFilter, RouteParams, updateQueryParams } from 'router';
import { dictionariesSelector } from 'store';
import { FilterModal } from 'common/components';
import { useIntl } from 'react-intl';
import {
  ContentType,
  Filter,
  Industry,
  Item,
  Tag as ITag,
} from './blog_page_models';
import { BlogPageSearch } from '../components/BlogPageSearch';
import { BlogPageBanner } from '../components/BlogPageBanner';
import { BlogPostCard } from '../components/BlogPostCard';
import { cn } from 'ui/utils/bem';

const cnBlogPage = cn('BlogPage');

type Props = RouteComponentProps<RouteParams>;
interface ModalMethods {
  show: () => void;
  hide: () => void;
}

const BlogPage: React.FC<Props> = ({ location, history }) => {
  const {
    industriesIds,
    contentTypes,
    tagsIds,
    name,
    mode = 'latest',
  } = getUrlFilter(location);

  const [contentItems, setContentItems] = useState<Item[]>([]);
  const [contentDefaultItems, setContentDefaultItems] = useState<Item[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { companySiteBlog } = useSelector(dictionariesSelector) || {};
  const {
    companySiteBlogIndustriesFilter,
    companySiteBlogTagsFilter,
    companySiteBlogContentTypesFilter,
    companySiteBlogInsightsTitle,
    companySiteBlogChosenTags,
    companySiteBlogChosenIndustries,
    companySiteBlogInsights: {
      companySiteBlogInsightsSearchLabel = 'Search',
      companySiteBlogInsightsModeLabel = 'Mode',
      companySiteBlogInsightsClearAll = 'Clear all',
    } = {},
    companySiteBlogNotFound: {
      companySiteBlogNotFoundTitle = "Oh no! We couldn't find anything.",
      companySiteBlogNotFoundText = "Try trending editor's picks:",
    } = {},
  } = companySiteBlog || {};

  const { locale } = useIntl();

  const {
    data,
    loading: loadingInitial,
    updateFilter,
    filter: filterData,
  } = useDataQuery(
    queryBlogPage,
    {
      locale,
      filter: {
        industriesIds: industriesIds,
        tagsIds: tagsIds,
        contentTypes: contentTypes,
        mode: mode,
        name: name,
      },
    },
    {
      skip: currentPage > 1,
    }
  );

  const {
    shortTags,
    industries: filterIndustries = [],
    tags: filterTags = [],
    contentTypes: filterContentTypes = [],
    filters: modes = [],
    pagesTotal = 1,
    items = [],
    defaultItems = [],
    banners,
    metaTitle,
  } = data || {};

  const [industriesSelected, setIndustriesSelected] = useState<Industry[]>([]);
  const [tagsSelected, setTagsSelected] = useState<ITag[]>([]);
  const [contentTypeSelected, setContentTypeSelected] = useState<ContentType[]>(
    []
  );
  const [modeSelected, setModesSelected] = useState<Filter>();
  const [nameSelected, setNameSelected] = useState(name);
  const [infinityRefVisible, setInfinityRefVisible] = useState<boolean>(false);

  const {
    pagination,
    updateFilter: updateFilterFeed,
    updatePagination,
    data: dataFeed,
    loading: loadingFeed,
    error,
    filter: filterCards,
  } = useDataPagination(
    queryBlogFeed,
    {
      locale,
      filter: {
        industriesIds: industriesIds,
        tagsIds: tagsIds,
        contentTypes: contentTypes,
        mode: mode,
        name: name,
      },
    },
    {
      skip: currentPage < 2,
    }
  );
  const changeFilter = useCallback(
    (filter: QueryFilter) => {
      if (filter.mode === 'data-and-analytics') {
        filter.tagsIds = ['244', '20'];
      }
      setCurrentPage(1);
      setInfinityRefVisible(false);
      setContentItems([]);
      setContentDefaultItems([]);
      updateFilter(filter);
      updateFilterFeed(filter);
    },
    [setInfinityRefVisible, updateFilter, updateFilterFeed, setContentItems]
  );

  useEffect(() => {
    if (items?.length > 0) {
      setContentItems(items);
      setInfinityRefVisible(true);
    } else if (defaultItems?.length > 0) {
      setContentDefaultItems(defaultItems);
    }
  }, [items]);

  useEffect(() => {
    if (dataFeed) {
      setContentItems(prev => [...prev, ...dataFeed.items]);
      setInfinityRefVisible(true);
    }
  }, [dataFeed]);

  useEffect(() => {
    setCurrentPage(pagination.page);
  }, [pagination]);

  // MODE FILTER
  useEffect(() => {
    setModesSelected(modes?.find(item => mode == item?.id));
  }, [history, modes]);

  // INDUSTRIES FILTER
  useEffect(() => {
    if (industriesIds) {
      const industriesIdsArray = Array.isArray(industriesIds)
        ? industriesIds
        : [industriesIds];
      setIndustriesSelected(
        filterIndustries?.filter(item =>
          industriesIdsArray?.some(i => item.id.toString() === i)
        )
      );
    }
  }, [history, filterIndustries]);

  // TAGS FILTER
  useEffect(() => {
    if (tagsIds) {
      const tagIdsArray = Array.isArray(tagsIds) ? tagsIds : [tagsIds];

      setTagsSelected(
        filterTags?.filter(item =>
          tagIdsArray.some(i => item.id.toString() === i)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTags, history]);

  // CONTENT_TYPE FILTER
  useEffect(() => {
    if (contentTypes) {
      const contentTypesParams = Array.isArray(contentTypes)
        ? contentTypes
        : [contentTypes];

      setContentTypeSelected(
        filterContentTypes?.filter(item =>
          contentTypesParams.some(i => item.id.toString() === i)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterContentTypes, history]);

  useEffect(() => {
    const { industriesIds, tagsIds, contentTypes, mode, name } =
      currentPage > 2 ? filterCards : filterData;

    updateQueryParams(history, {
      industriesIds,
      tagsIds,
      contentTypes,
      mode,
      name,
    });
  }, [filterCards, history, filterData]);

  const [sentryRef] = useInfiniteScroll({
    loading: loadingFeed,
    hasNextPage: pagination?.page < pagesTotal,
    onLoadMore: () => {
      updatePagination({ page: pagination.page + 1 });
    },
    disabled: !!error,
  });

  const filterModalRef = React.useRef<ModalMethods>(null);

  const resetForm = () => {
    setTagsSelected([]);
    setContentTypeSelected([]);
    setIndustriesSelected([]);
    setModesSelected(modes[1]);
    setNameSelected('');
    changeFilter({
      industriesIds: '',
      tagsIds: '',
      contentTypes: '',
      name: '',
      mode: 'latest',
    });
    if (filterModalRef.current) {
      hideModalFilter();
    }
  };

  const submitModalForm = () => {
    hideModalFilter();
    changeFilter({
      industriesIds: industriesSelected?.map(item => item.id.toString()),
      tagsIds: tagsSelected?.map(item => item.id.toString()),
      contentTypes: contentTypeSelected?.map(item => item.id.toString()),
      mode: modeSelected?.id,
    });
  };

  const showModalFilter = () => {
    if (filterModalRef.current) {
      filterModalRef.current.show();
    }
  };

  const hideModalFilter = () => {
    if (filterModalRef.current) {
      filterModalRef.current.hide();
    }
  };

  useGTMDataSend(metaTitle);

  return (
    <>
      <div className={cnBlogPage()}>
        <div className="container">
          <Grid
            breakpoints={{
              desktop: { cols: 12, gap: 'xl' },
              tablet: { cols: 12, gap: 'xl' },
              mobile: { cols: 12, gap: 'xl' },
            }}
          >
            <GridItem
              breakpoints={{
                desktop: { col: 12 },
                tablet: { col: 12 },
                mobile: { col: 12 },
              }}
              className={cnBlogPage('Top')}
            >
              <div className={cnBlogPage('Main-Title')}>
                <Typography.Title size="lg" as="h1">
                  {companySiteBlogInsightsTitle}
                </Typography.Title>
                <Mq query="--tablet-max">
                  <Button
                    view="ghost"
                    iconRight={IconFilter}
                    active={
                      tagsSelected?.length > 0 ||
                      industriesSelected?.length > 0 ||
                      contentTypeSelected?.length > 0
                    }
                    onClick={showModalFilter}
                    className={cnBlogPage('Filters-Mobile-Button')}
                  />
                  <FilterModal
                    ref={filterModalRef}
                    onSubmitForm={submitModalForm}
                    onResetForm={resetForm}
                    clearAllLabel={companySiteBlogInsightsClearAll}
                    modes={{
                      modesLabel: companySiteBlogInsightsModeLabel,
                      modes: modes,
                      modeSelected: modeSelected,
                      modeChange: ({ value }) => {
                        setModesSelected(value);
                      },
                    }}
                    industries={{
                      title: companySiteBlogIndustriesFilter,
                      contentItems: filterIndustries,
                      onChange: ({ value }) => {
                        setIndustriesSelected(value);
                      },
                      value: industriesSelected,
                    }}
                    tags={{
                      title: companySiteBlogTagsFilter,
                      contentItems: filterTags,
                      onChange: ({ value }) => {
                        setTagsSelected(value);
                      },
                      value: tagsSelected,
                    }}
                    contentTypes={{
                      title: companySiteBlogContentTypesFilter,
                      contentItems: filterContentTypes,
                      onChange: ({ value }) => {
                        setContentTypeSelected(value);
                      },
                      value: contentTypeSelected,
                    }}
                  />
                </Mq>
              </div>
              <div className={cnBlogPage('Search')}>
                <BlogPageSearch
                  placeholder={companySiteBlogInsightsSearchLabel}
                  nameSelected={nameSelected}
                  setNameSelected={setNameSelected}
                  nameChange={value => {
                    changeFilter({
                      name: value,
                    });
                  }}
                />
              </div>
              <Mq query="--tablet-max">
                <div className={cnBlogPage('Controls')}>
                  {(tagsSelected?.length > 0 ||
                    industriesSelected?.length > 0 ||
                    contentTypeSelected?.length > 0) && (
                    <div className={cnBlogPage('Controls-Selected')}>
                      <Typography.Text
                        size="xs"
                        weight="bold"
                        transform="uppercase"
                        className={cnBlogPage('Controls-Selected-Title')}
                        as="h2"
                      >
                        Search results for:
                      </Typography.Text>
                      <div className={cnBlogPage('Controls-Selected-List')}>
                        {tagsSelected?.length > 0 &&
                          tagsSelected.map(item => (
                            <div key={item.id + item.title}>
                              <Tag
                                onCancel={() => {
                                  const actualArray = tagsSelected.filter(
                                    w => w.id !== item.id
                                  );
                                  setTagsSelected(actualArray);
                                  changeFilter({
                                    tagsIds: actualArray?.map(item =>
                                      item.id.toString()
                                    ),
                                  });
                                }}
                                label={item.title}
                                mode="cancel"
                              />
                            </div>
                          ))}

                        {industriesSelected?.length > 0 &&
                          industriesSelected.map(item => (
                            <div key={item.id + item.title}>
                              <Tag
                                onCancel={() => {
                                  const actualArray = industriesSelected.filter(
                                    w => w.id !== item.id
                                  );
                                  setIndustriesSelected(actualArray);
                                  changeFilter({
                                    industriesIds: actualArray?.map(item =>
                                      item.id.toString()
                                    ),
                                  });
                                }}
                                label={item.title}
                                mode="cancel"
                              />
                            </div>
                          ))}

                        {contentTypeSelected?.length > 0 &&
                          contentTypeSelected.map(item => (
                            <div key={item.id + item.title}>
                              <Tag
                                onCancel={() => {
                                  const actualArray = contentTypeSelected.filter(
                                    w => w.id !== item.id
                                  );
                                  setContentTypeSelected(actualArray);
                                  changeFilter({
                                    contentTypes: actualArray?.map(item =>
                                      item.id.toString()
                                    ),
                                  });
                                }}
                                label={item.title}
                                mode="cancel"
                              />
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </Mq>
            </GridItem>
          </Grid>
          <Grid
            breakpoints={{
              desktop: { cols: 12, gap: 'xl' },
              tablet: { cols: 12, gap: 'xl' },
              mobile: { cols: 12, gap: 'xl' },
            }}
            className={cnBlogPage('Content')}
          >
            <Mq query="--desktop">
              <GridItem
                breakpoints={{
                  desktop: { col: 3 },
                  tablet: { col: 12 },
                  mobile: { col: 12 },
                }}
                className={cnBlogPage('Desktop-Filters')}
              >
                {data && (
                  <DesktopFilters
                    modesLabel={companySiteBlogInsightsModeLabel}
                    modes={modes}
                    modeSelected={modeSelected}
                    modeChange={({ value }) => {
                      setModesSelected(value);
                      changeFilter({
                        mode: value.id,
                      });
                    }}
                    industries={filterIndustries}
                    industriesSelected={industriesSelected}
                    industriesPlaceholder={companySiteBlogIndustriesFilter}
                    industriesPlaceholderSelected={
                      companySiteBlogChosenIndustries
                    }
                    industriesChange={({ value }) => {
                      setIndustriesSelected(value);
                      changeFilter({
                        industriesIds: value?.map(item => item.id.toString()),
                      });
                    }}
                    // moreTags={filterTags}
                    contentTypes={filterContentTypes}
                    contentTypePlaceholder={companySiteBlogContentTypesFilter}
                    contentTypesPlaceholderSelected={'chosen content types'}
                    contentTypesSelected={contentTypeSelected}
                    contentTypesChange={({ value }) => {
                      setContentTypeSelected(value);
                      changeFilter({
                        contentTypes: value?.map(item => item.id.toString()),
                      });
                    }}
                    tags={shortTags}
                    moreTags={filterTags}
                    moreTagsButton={'see all'}
                    tagsPlaceholder={companySiteBlogTagsFilter}
                    tagsPlaceholderSelected={companySiteBlogChosenTags}
                    tagsSelected={tagsSelected}
                    tagsChange={({ value }) => {
                      setTagsSelected(value);
                      changeFilter({
                        tagsIds: value?.map(item => item.id.toString()),
                      });
                    }}
                    onResetForm={resetForm}
                    clearAllLabel={companySiteBlogInsightsClearAll}
                  />
                )}
              </GridItem>
            </Mq>
            <GridItem
              breakpoints={{
                desktop: { col: 9 },
                tablet: { col: 12 },
                mobile: { col: 12 },
              }}
            >
              {contentItems?.length === 0 && !loadingInitial && (
                <div className={cnBlogPage('Error')}>
                  <Typography.Title size="md">
                    {companySiteBlogNotFoundTitle}{' '}
                    <span>{companySiteBlogNotFoundText}</span>
                  </Typography.Title>
                </div>
              )}
              <Grid
                breakpoints={{
                  desktop: { cols: 12, gap: 'xl' },
                  tablet: { cols: 12, gap: 'xl' },
                  mobile: { cols: 12, gap: 'xl' },
                }}
                className={cnBlogPage('Content-List')}
              >
                {loadingInitial && (
                  <GridItem
                    breakpoints={{
                      desktop: { col: 4 },
                      tablet: { col: 4 },
                      mobile: { col: 12 },
                    }}
                  >
                    <Loader />
                  </GridItem>
                )}
                {contentItems || contentDefaultItems
                  ? (() => {
                      const itemsArray =
                        contentItems.length > 0
                          ? contentItems
                          : contentDefaultItems;
                      const filteredItems = itemsArray.filter(
                        post => Object.values(post).filter(Boolean).length > 1
                      );
                      const itemsToRender: React.ReactNode[] = [];
                      let bannerIndex = 0;

                      filteredItems.forEach((post, index) => {
                        itemsToRender.push(
                          <GridItem
                            key={post.id}
                            breakpoints={{
                              desktop: { col: 4 },
                              tablet: { col: 6 },
                              mobile: { col: 12 },
                            }}
                          >
                            <BlogPostCard {...post} />
                          </GridItem>
                        );

                        if (
                          (index + 1) % 6 === 0 &&
                          banners &&
                          bannerIndex < banners.length
                        ) {
                          itemsToRender.push(
                            <GridItem
                              key={`banner-${bannerIndex}`}
                              breakpoints={{
                                desktop: { col: 12 },
                                tablet: { col: 12 },
                                mobile: { col: 12 },
                              }}
                            >
                              <BlogPageBanner {...banners[bannerIndex]} />
                            </GridItem>
                          );
                          bannerIndex = (bannerIndex + 1) % banners.length;
                        }
                      });
                      return itemsToRender;
                    })()
                  : null}
              </Grid>
              {loadingFeed && (
                <SiteBlock topGap="xxl">
                  <Loader />
                </SiteBlock>
              )}
              {infinityRefVisible && pagesTotal > currentPage && (
                <div ref={sentryRef} />
              )}
            </GridItem>
          </Grid>
        </div>
      </div>
    </>
  );
};

export default BlogPage;
